import { useEffect, useState } from "react";
import { DecisionInput } from "@/components/decision/DecisionInput";
import { QuestionCard } from "@/components/decision/QuestionCard";
import { FinalDecision } from "@/components/decision/FinalDecision";
import { LoadingScreen } from "@/components/decision/LoadingScreen";
import { LanguageSelector } from "@/components/decision/LanguageSelector";
import { ThemeToggle } from "@/components/decision/ThemeToggle";
import { AppMenu } from "@/components/navigation/AppMenu";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Language } from "@/utils/translations";

// Define available themes
const themes = [
  "decision-theme-blue",
  "decision-theme-purple",
  "decision-theme-green",
  "decision-theme-orange",
];

const Decision = () => {
  const [step, setStep] = useState<
    "input" | "questions" | "loading" | "final" | "generating"
  >("input");
  const [decision, setDecision] = useState("");
  const [questions, setQuestions] = useState<string[]>([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<boolean[]>([]);
  const [finalDecision, setFinalDecision] = useState<"yes" | "no">("yes");
  const [explanation, setExplanation] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const [currentLang, setCurrentLang] = useState<Language>("en");
  const [currentTheme, setCurrentTheme] = useState<string>("");

  // Get the current language when component mounts
  useState(() => {
    const savedLang = localStorage.getItem("preferredLanguage") as Language;
    if (savedLang) {
      setCurrentLang(savedLang);
    }
  });

  useEffect(() => {
    // Randomly select a theme when the component mounts
    const randomTheme = themes[Math.floor(Math.random() * themes.length)];
    setCurrentTheme(randomTheme);

    // Apply the theme to the document
    document.documentElement.classList.remove(...themes);
    document.documentElement.classList.add(randomTheme);

    // Cleanup when component unmounts
    return () => {
      document.documentElement.classList.remove(...themes);
    };
  }, []);

  const generateQuestions = async (decision: string, questionCount: number) => {
    setIsLoading(true);
    setStep("generating");
    try {
      console.log("Generating questions for decision:", decision);
      const { data, error } = await supabase.functions.invoke(
        "generate-questions ",
        {
          body: { decision, questionCount, language: currentLang },
        }
      );

      if (error) throw error;
      console.log("Questions generated:", data);

      const questionList = data.questions;
      setQuestions(questionList);
      setStep("questions");
    } catch (error) {
      console.error("Error generating questions:", error);
      toast({
        title: "Error",
        description: "Failed to generate questions. Please try again.",
        variant: "destructive",
      });
      setStep("input");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDecisionSubmit = (
    inputDecision: string,
    questionCount: number
  ) => {
    setDecision(inputDecision);
    generateQuestions(inputDecision, questionCount);
  };

  const handleAnswer = async (answer: boolean) => {
    const newAnswers = [...answers, answer];
    setAnswers(newAnswers);

    if (currentQuestion === questions.length - 1) {
      setStep("loading");
      try {
        const { data, error } = await supabase.functions.invoke(
          "generate-decision",
          {
            body: {
              decision,
              questions,
              answers: newAnswers,
              language: currentLang,
            },
          }
        );

        if (error) throw error;
        console.log("Final decision data:", data);

        setFinalDecision(data.decision === "yes" ? "yes" : "no");
        setExplanation(data.explanation || "");
        setStep("final");
      } catch (error) {
        console.error("Error getting final decision:", error);
        toast({
          title: "Error",
          description: "Failed to generate final decision. Please try again.",
          variant: "destructive",
        });
        setStep("questions");
      }
    } else {
      setCurrentQuestion((prev) => prev + 1);
    }
  };

  const resetApp = () => {
    setStep("input");
    setDecision("");
    setQuestions([]);
    setCurrentQuestion(0);
    setAnswers([]);
    setExplanation("");
  };

  return (
    <div className="min-h-screen transition-colors duration-300 text-foreground">
      <div className="absolute top-4 right-4 grid grid-cols-2 gap-2 z-10">
        <LanguageSelector />
        <AppMenu />
        {/* <ThemeToggle /> */}
      </div>
      {step === "input" && (
        <DecisionInput onSubmit={handleDecisionSubmit} isLoading={isLoading} />
      )}
      {step === "generating" && <LoadingScreen type="generating" />}
      {step === "questions" && questions[currentQuestion] && (
        <QuestionCard
          question={questions[currentQuestion]}
          onAnswer={handleAnswer}
          progress={(currentQuestion / questions.length) * 100}
        />
      )}
      {step === "loading" && <LoadingScreen type="deciding" />}
      {step === "final" && (
        <FinalDecision
          question={decision}
          decision={finalDecision}
          explanation={explanation}
          onReset={resetApp}
        />
      )}
    </div>
  );
};

export default Decision;
